import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// Booking categories (base ticket line items) eligible for a spontaneous reduction.
// Only priced categories qualify: the reduction keeps the full price due and issues a goodwill
// voucher for the value of the missing PAID tickets. A free seat (e.g. a zero-priced "Freispieler"
// / group free ticket) refunds nothing, so it must not be offered or auto-selected as the category
// to reduce — otherwise the dialog defaulted to the free category instead of the paying players.
const getBookingCategories = booking =>
  (booking?.lineItems || [])
    .filter(
      li =>
        li.type === 'booking' &&
        li.status === 'active' &&
        (li.count || 0) > 0 &&
        (Number(li.price) || 0) > 0
    )
    .map(li => ({
      categoryId: String(li.categoryId),
      name: (li.description || '').split(' - ')[0] || 'Kategorie',
      count: li.count || 0,
      price: Number(li.price) || 0,
    }));

// Dialog that explains the spontaneous reduction (full price stays due, customer gets a
// MOP-show voucher minus ticket insurance, upsells/packages of the absentees are dropped)
// and lets staff pick the category and number of absent people.
function SpontaneousReductionDialog({
  open,
  onClose,
  onConfirm,
  onPreview,
  submitting = false,
  booking,
}) {
  const categories = useMemo(() => getBookingCategories(booking), [booking]);
  const [categoryId, setCategoryId] = useState('');
  const [count, setCount] = useState(1);

  useEffect(() => {
    if (open) {
      setCategoryId(categories[0]?.categoryId || '');
      setCount(1);
    }
  }, [open, categories]);

  const selectedCategory = categories.find(c => c.categoryId === categoryId) || categories[0];
  const totalParticipants = booking?.participantCount || 0;
  // At least one participant must remain on the booking overall.
  const maxReducible = Math.max(
    0,
    Math.min(selectedCategory ? selectedCategory.count : 0, totalParticipants - 1)
  );
  const estimatedBase = selectedCategory ? selectedCategory.price * count : 0;

  // The MOP-show voucher is only issued once the booking is genuinely fully paid. For an unpaid
  // booking (typically an offline/cash booking whose money has not been collected yet) the reduction
  // is recorded now and the voucher is "vorgemerkt" — issued once staff mark the booking as paid.
  const isFullyPaid = !!booking?.fullyPaid;
  const hasOpenOfflinePayment = useMemo(
    () =>
      (booking?.lineItems || []).some(
        li =>
          li.status === 'active' &&
          (li.paymentReferences || []).some(r => r.paymentType === 'offline' && !r.isPaid)
      ),
    [booking]
  );

  // Exact voucher value from the backend (it deducts the product-linked ticket-insurance share,
  // which the frontend booking payload does not carry). Debounced on category/count changes; a
  // sequence guard prevents an older response from overwriting a newer one.
  const [preview, setPreview] = useState(null);
  const previewSeqRef = useRef(0);
  useEffect(() => {
    if (!open || !onPreview || !selectedCategory || !(count >= 1) || count > maxReducible) {
      setPreview(null);
      return;
    }
    const seq = ++previewSeqRef.current;
    setPreview(prev => ({ ...(prev || {}), loading: true }));
    const handle = setTimeout(async () => {
      try {
        const res = await onPreview(selectedCategory.categoryId, count);
        if (seq !== previewSeqRef.current) return;
        setPreview({
          loading: false,
          voucherValue: typeof res?.voucherValue === 'number' ? res.voucherValue : null,
          insuranceDeducted: !!res?.insuranceDeducted,
          eligible: res?.eligible !== false,
          message: res?.message || null,
        });
      } catch {
        if (seq !== previewSeqRef.current) return;
        setPreview({ loading: false, error: true });
      }
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, categoryId, count, maxReducible, selectedCategory?.categoryId]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const canConfirm =
    !submitting &&
    !!selectedCategory &&
    count >= 1 &&
    count <= maxReducible &&
    preview?.eligible !== false;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Spontan weniger Personen</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant='body2'>
            Es sind weniger Personen erschienen als gebucht. Die fehlenden Plätze werden als
            Gutschein erstattet — der <strong>volle Buchungspreis bleibt jedoch bestehen</strong>.
          </Typography>
          <Alert severity='info'>
            <Stack spacing={1}>
              <Typography variant='body2'>
                • Der <strong>volle Preis</strong> ist auch bei <strong>Barzahlung</strong> fällig.
              </Typography>
              <Typography variant='body2'>
                • Der Kunde erhält einen <strong>Gutschein</strong> über den Wert der stornierten
                Tickets — <strong>abzüglich der anteiligen Ticketversicherung</strong>.
              </Typography>
              <Typography variant='body2'>
                • Zugehörige <strong>Upsells/Pakete</strong> der fehlenden Personen werden storniert
                und nicht berechnet.
              </Typography>
              <Typography variant='body2'>
                • Den Gutschein <strong>nicht aktiv anbieten</strong> — erst ausstellen, wenn der
                Kunde von sich aus hinterfragt, warum der volle Preis fällig ist.
              </Typography>
            </Stack>
          </Alert>

          {!isFullyPaid && (
            <Alert severity='warning'>
              <Stack spacing={1}>
                <Typography variant='body2'>
                  • Diese Buchung ist <strong>noch nicht vollständig bezahlt</strong>
                  {hasOpenOfflinePayment ? ' (offene Vor-Ort-/Barzahlung)' : ''}.
                </Typography>
                <Typography variant='body2'>
                  • Der Gutschein wird <strong>vorgemerkt</strong> und erst ausgestellt, sobald die
                  Buchung als <strong>vollständig bezahlt</strong> markiert wurde.
                </Typography>
              </Stack>
            </Alert>
          )}

          {categories.length > 1 && (
            <FormControl fullWidth size='small'>
              <InputLabel id='spontaneous-reduction-category-label'>Kategorie</InputLabel>
              <Select
                labelId='spontaneous-reduction-category-label'
                label='Kategorie'
                value={categoryId}
                onChange={e => setCategoryId(e.target.value)}
              >
                {categories.map(c => (
                  <MenuItem key={c.categoryId} value={c.categoryId}>
                    {c.name} ({c.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            type='number'
            size='small'
            label='Anzahl fehlender Personen'
            value={count}
            onChange={e => {
              const v = parseInt(e.target.value, 10);
              const safeMax = maxReducible || 1;
              setCount(Number.isFinite(v) ? Math.max(1, Math.min(v, safeMax)) : 1);
            }}
            inputProps={{ min: 1, max: maxReducible }}
            helperText={
              maxReducible > 0
                ? `Maximal ${maxReducible} (mind. 1 Teilnehmer verbleibt)`
                : 'Reduktion nicht möglich (mind. 1 Teilnehmer)'
            }
          />

          {selectedCategory && count >= 1 && (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              {preview?.loading
                ? 'Gutscheinwert wird berechnet …'
                : preview && typeof preview.voucherValue === 'number'
                  ? `Gutscheinwert: ${preview.voucherValue.toFixed(2)} €${
                      preview.insuranceDeducted ? ' (abzüglich anteiliger Ticketversicherung)' : ''
                    }`
                  : `Voraussichtlicher Gutscheinwert: ca. ${estimatedBase.toFixed(2)} € (abzüglich Ticketversicherung; der exakte Betrag wird berechnet).`}
              {!isFullyPaid && ' Ausstellung erst nach vollständiger Bezahlung.'}
            </Typography>
          )}

          {preview && preview.eligible === false && preview.message && (
            <Alert severity='error'>{preview.message}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          color='warning'
          onClick={() => onConfirm(count, selectedCategory?.categoryId)}
          disabled={!canConfirm}
        >
          {submitting ? (
            <CircularProgress size={18} color='inherit' />
          ) : isFullyPaid ? (
            'Reduzieren & Gutschein'
          ) : (
            'Reduzieren & Gutschein vormerken'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SpontaneousReductionDialog;
